import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Download, Loader2, LogOut, Printer } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, LOGO_URL } from "@/lib/brand";
import { authHeader, currentEdition, editionLabel, errText, stageOf } from "@/lib/admin";
import { EntryRow } from "@/components/admin/EntryRow";

const PLAN_PILLS = [["", "All plans"], ["1m", "Monthly"], ["3m", "3 months"], ["6m", "6 months"], ["12m", "12 months"], ["1x", "One-time"]];
const STAGES = [["all", "All"], ["to_pack", "To pack"], ["packed", "Packed"], ["posted", "Posted"]];

const Pills = ({ options, value, onChange, prefix, counts }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([v, l]) => (
      <button key={l} onClick={() => onChange(v)} className={`rounded-full px-4 py-2 text-sm border transition-colors ${value === v ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 hover:border-amber-500"}`} data-testid={`${prefix}-${v || "all"}`}>
        {l}{counts ? ` · ${counts[v]}` : ""}
      </button>
    ))}
  </div>
);

export const Entries = ({ token, onLogout }) => {
  const [edition, setEdition] = useState(currentEdition());
  const [plan, setPlan] = useState("");
  const [stage, setStage] = useState("all");
  const [rows, setRows] = useState(null);
  const q = `?edition=${edition}${plan ? `&plan=${plan}` : ""}`;

  const load = useCallback(async () => {
    setRows(null);
    try { setRows((await axios.get(`${API}/admin/entries${q}`, authHeader(token))).data); }
    catch (ex) { if (ex.response?.status === 401) onLogout(); else toast.error(errText(ex)); }
  }, [q, token, onLogout]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: rows?.length || 0, to_pack: 0, packed: 0, posted: 0 };
    (rows || []).forEach((r) => { c[stageOf(r)] += 1; });
    return c;
  }, [rows]);
  const shown = (rows || []).filter((r) => stage === "all" || stageOf(r) === stage);

  const onDispatch = async (r, kind, value) => {
    try {
      const { data } = await axios.patch(`${API}/admin/entries/${r.id}/dispatch`, { edition, [kind]: value }, authHeader(token));
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, packed_at: data.packed_at, posted_at: data.posted_at } : x)));
    } catch (ex) { toast.error(errText(ex)); }
  };

  const download = async () => {
    try {
      const res = await axios.get(`${API}/admin/entries.csv${q}`, { ...authHeader(token), responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `little-lantern-${edition}${plan ? `-${plan}` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (ex) { toast.error(errText(ex)); }
  };

  const printLabels = () => window.open(`/admin/labels${q}&stage=${stage}`, "_blank");

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src={LOGO_URL} alt="" className="h-14 w-auto object-contain" /><span className="font-serif-display text-2xl">Packing desk</span></div>
          <button onClick={onLogout} className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900" data-testid="admin-logout-button"><LogOut className="h-4 w-4" />Sign out</button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-800 font-semibold">Edition</p>
            <p className="font-serif-display text-4xl mt-1" data-testid="admin-edition-label">{editionLabel(edition)}</p>
          </div>
          <div className="flex items-center gap-3">
            <Input type="month" value={edition} onChange={(e) => e.target.value && setEdition(e.target.value)} className="bg-white w-44" data-testid="admin-edition-input" />
            <button onClick={printLabels} disabled={!shown.length} className="rounded-full border border-stone-900 px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-stone-900 hover:text-white transition-colors disabled:opacity-40" data-testid="admin-print-labels-button"><Printer className="h-4 w-4" />Print labels ({shown.length})</button>
            <button onClick={download} className="rounded-full bg-stone-900 text-white px-5 py-2.5 text-sm flex items-center gap-2 hover:bg-amber-600 transition-colors" data-testid="admin-download-csv-button"><Download className="h-4 w-4" />CSV</button>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Pills options={STAGES} value={stage} onChange={setStage} prefix="admin-stage" counts={counts} />
          <Pills options={PLAN_PILLS} value={plan} onChange={setPlan} prefix="admin-plan" />
        </div>
        <p className="text-xs text-stone-500">Subscribers (every subscription ships one envelope a month) appear in every edition from the month they joined. One-time envelopes appear until they're posted, then disappear from later editions. Entries marked "sent to payment" are not confirmed by the site, so match them against your Razorpay dashboard before posting.</p>
        <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto">
          {!rows ? <Loader2 className="h-6 w-6 animate-spin m-10" /> : shown.length === 0 ? (
            <p className="p-10 text-stone-500" data-testid="admin-empty">Nothing here for this edition.</p>
          ) : (
            <Table data-testid="admin-entries-table">
              <TableHeader><TableRow>{["Packed", "Posted", "Joined", "Plan", "Name", "Contact", "Address", "Note"].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>{shown.map((r) => <EntryRow key={r.id} r={r} onDispatch={onDispatch} />)}</TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
};
